import { useMemo } from 'react'

import { getDateString, getDaysSublabel, getFormattedDate, parseDateOnly } from 'utils'

import { COBRA_DEADLINE_DAYS } from './constants'
import { Timeline, type TimelineEvent } from './Timeline'

interface OffboardingTimelineProps {
  employeeName: string
  finalDayOfEmployment: string | null
  cobraEligible: boolean | null
  willReceiveOffboardingEmail: boolean
}

export function OffboardingTimeline ({ employeeName, finalDayOfEmployment, cobraEligible, willReceiveOffboardingEmail }: OffboardingTimelineProps) {
  // Get the first name safely (with fallback for empty/missing names)
  const firstName = employeeName?.split(' ')?.[0] || 'Employee'

  const events = useMemo<TimelineEvent[]>(() => {
    if (!finalDayOfEmployment) return []

    // Use ISO date strings for consistent timezone-safe comparison
    const todayISO = new Date().toISOString().split('T')[0]
    const finalDate = parseDateOnly(finalDayOfEmployment)
    const finalDateISO = finalDayOfEmployment.split('T')[0] // Already in YYYY-MM-DD format
    const timelineEvents: TimelineEvent[] = []

    // Compare ISO strings to avoid timezone issues
    const isFuture = finalDateISO > todayISO

    if (isFuture) {
      // FUTURE OFFBOARDING: finalDate > today
      // Email goes out on the final day

      // Event 1: Today - confirmation
      timelineEvents.push({
        label: 'Today',
        description: `${firstName}'s offboarding will be confirmed upon completing this flow.`,
      })

      // Event 2: Final day - always show
      if (willReceiveOffboardingEmail) {
        // Show with email message
        const emailMessage = cobraEligible
          ? `${firstName}'s benefits on Thatch will end, and they'll get an email with next steps. This email will include COBRA enrollment details.`
          : `${firstName}'s benefits on Thatch will end, and they'll get an email with next steps.`

        timelineEvents.push({
          label: getFormattedDate(finalDayOfEmployment, { year: undefined }),
          sublabel: getDaysSublabel(finalDate),
          description: emailMessage,
        })

        // Event 3: COBRA deadline - 60 days from final day (if eligible)
        if (cobraEligible) {
          const cobraDeadline = new Date(finalDate)
          cobraDeadline.setDate(cobraDeadline.getDate() + COBRA_DEADLINE_DAYS)
          timelineEvents.push({
            label: getFormattedDate(getDateString(cobraDeadline), { year: undefined }),
            sublabel: getDaysSublabel(cobraDeadline),
            description: `${firstName} has ${COBRA_DEADLINE_DAYS} days after receiving communications from Thatch to make a COBRA election.`,
          })
        }
      } else {
        // Show without email, just last day of employment
        timelineEvents.push({
          label: getFormattedDate(finalDayOfEmployment, { year: undefined }),
          sublabel: getDaysSublabel(finalDate),
          description: `${firstName}'s last day of employment.`,
        })
      }
    } else {
      // TODAY OR RETROACTIVE OFFBOARDING: finalDate <= today
      // Email goes out tomorrow (we only send emails once a day in the morning)

      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)

      // Event 1: Final day (past or today)
      timelineEvents.push({
        label: getFormattedDate(finalDayOfEmployment, { year: undefined }),
        sublabel: getDaysSublabel(finalDate),
        description: `${firstName}'s last day of employment.`,
      })

      // Event 2: Today - confirmation
      timelineEvents.push({
        label: 'Today',
        description: `${firstName}'s offboarding will be confirmed upon completing this flow.`,
      })

      // Event 3: Tomorrow - email goes out (only if they'll receive email)
      if (willReceiveOffboardingEmail) {
        const emailMessage = cobraEligible
          ? `${firstName}'s benefits on Thatch will end, and they'll get an email with next steps. This email will include COBRA enrollment details.`
          : `${firstName}'s benefits on Thatch will end, and they'll get an email with next steps.`

        timelineEvents.push({
          label: getFormattedDate(getDateString(tomorrow), { year: undefined }),
          sublabel: 'Tomorrow',
          description: emailMessage,
        })

        // Event 4: COBRA deadline - 60 days from tomorrow (if eligible)
        if (cobraEligible) {
          const cobraDeadline = new Date(tomorrow)
          cobraDeadline.setDate(cobraDeadline.getDate() + COBRA_DEADLINE_DAYS)
          timelineEvents.push({
            label: getFormattedDate(getDateString(cobraDeadline), { year: undefined }),
            sublabel: getDaysSublabel(cobraDeadline),
            description: `${firstName} has ${COBRA_DEADLINE_DAYS} days after receiving communications from Thatch to make a COBRA election.`,
          })
        }
      }
    }

    return timelineEvents
  }, [firstName, finalDayOfEmployment, cobraEligible, willReceiveOffboardingEmail])

  if (events.length === 0) return null

  const subtitle = !willReceiveOffboardingEmail
    ? `${firstName} will not receive an offboarding email because they do not have active coverage.`
    : undefined

  return <Timeline events={events} subtitle={subtitle} />
}
